import * as Sentry from "@sentry/react"

declare global {
  interface Window {
    rails_env: string
    gon?: {
      environment?: string
      user?: {
        id: number
        email: string
        full_name: string
      }
      ip_address?: string
      ip_country?: string
      sentry_dsn?: string
    }
  }
}

let isInitialized = false

/**
 * Initialize Sentry for React components.
 * This is designed to work alongside the existing Sentry browser SDK
 * that's loaded via script tag for general JavaScript error tracking.
 *
 * This initialization focuses on React-specific error boundaries
 * and component lifecycle errors.
 */
export function initSentryReact(): void {
  // Only initialize once
  if (isInitialized) {
    return
  }

  // Only initialize in production or staging
  const env = window.rails_env || window.gon?.environment
  const dsn = window.gon?.sentry_dsn
  let tracesSampleRate = 0
  if (env === "development") {
    tracesSampleRate = 1.0
  }

  try {
    Sentry.init({
      dsn,
      environment: env,
      // React-specific integrations will be added automatically
      integrations: [Sentry.spotlightBrowserIntegration()],
      // Sample rates - keep low to avoid overwhelming the service
      tracesSampleRate,
      // Only send a sample of errors
      // Tag all events from this integration
      // Ignore common noise errors
      ignoreErrors: [
        // Random plugins/extensions
        "top.GLOBALS",
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "http://tt.epicplay.com",
        "Can't find variable: ZiteReader",
        "jigsaw is not defined",
        "ComboSearch is not defined",
        "http://loading.retry.widdit.com/",
        "atomicFindClose",
        // Facebook borked
        "fb_xd_fragment",
        // ISP "optimizing" proxy
        "bmi_SafeAddOnload",
        "EBCallBackMessageReceived",
        // Toolbar
        "conduitPage",
        // Custom ones
        "Can't execute code from freed script",
        "CORS error",
        // React-specific noise
        "ResizeObserver loop",
        "ResizeObserver loop completed with undelivered notifications",
      ],
      denyUrls: [
        // Facebook flakiness
        /graph\.facebook\.com/i,
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i,
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        // Custom ones
        /v1\/risk\.js/i,
        /gc\.kis\.v2\.scr\.kaspersky-labs\.com\//i,
        /frame\..*\.js/i,
        /js\.intercomcdn\.com/i,
        /static\.cloudflareinsights\.com/i,
      ],
      beforeSend(event: Sentry.Event, hint: Sentry.EventHint) {
        // There is no easy way to determine the type of error, so we just check if there is stimulus controller
        // in the stacktrace. If there is, it's a stimulus error, otherwise it's a react error.
        const type =
          event.exception?.values?.[0]?.stacktrace?.frames
            ?.map((x) => x?.filename)
            .filter((x) => x?.includes("_controller"))?.length > 0
            ? "stimulus"
            : "react"
        if (type !== "react") {
          return null
        }
        console.log(type)
        const error = hint.originalException
        if (error instanceof Error) {
          event.tags = {
            ...event.tags,
            errorType: error.name,
          }
        }
        return event
      },
    })

    // Set user context if available
    if (window.gon?.user) {
      Sentry.setUser({
        id: String(window.gon.user.id),
      })
    }

    // Add additional context
    if (window.gon?.ip_country) {
      Sentry.setTag("ip_country", window.gon.ip_country)
    }

    isInitialized = true
  } catch (error) {
    console.error("[Sentry React] Failed to initialize:", error)
    isInitialized = true
  }
}

/**
 * Capture an exception manually with React context
 */
export function captureReactException(
  error: Error,
  componentName?: string,
  extraContext?: Record<string, unknown>,
): void {
  Sentry.withScope((scope) => {
    scope.setTag("source", "react")
    if (componentName) {
      scope.setTag("component", componentName)
    }
    if (extraContext) {
      scope.setExtras(extraContext)
    }
    Sentry.captureException(error)
  })
}

/**
 * Log a message to Sentry
 */
export function captureReactMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
  extraContext?: Record<string, unknown>,
): void {
  Sentry.withScope((scope) => {
    scope.setTag("source", "react")
    scope.setLevel(level)
    if (extraContext) {
      scope.setExtras(extraContext)
    }
    Sentry.captureMessage(message)
  })
}

// Re-export Sentry for direct use
export { Sentry }
